/**
 * useFtmStore — Per-node Follow-the-Money cache.
 *
 * Each canvas node that has had FTM run at least once gets a cached result
 * here keyed by `${chain}:${address}`. The Analysis tab's Follow-the-Money
 * section reads this store to decide whether to show:
 *   - the "Run" button (no entry yet), or
 *   - a summary card + "View Report" + "Re-run" (entry cached).
 *
 * The store is serialized into `graph_state.ftm_cache` when an investigation
 * is saved, and re-hydrated on load — so officers never lose their FTM
 * results across sessions.
 */
import { create } from 'zustand';

export interface FtmCacheEntry {
    chain: string;
    address: string;
    response: any;         // raw auto-trace response (paths, edges, targets)
    ranAt: string;         // ISO timestamp
    /** Whether this entry has been archived to the investigation artifacts yet. */
    archived?: boolean;
}

interface FtmStoreState {
    /** Keyed by `${chainUpper}:${address}` for stable, case-correct lookup. */
    entries: Record<string, FtmCacheEntry>;
}

interface FtmStoreActions {
    getEntry: (chain: string | null | undefined, address: string | null | undefined) => FtmCacheEntry | null;
    setEntry: (chain: string, address: string, response: any) => void;
    clearEntry: (chain: string, address: string) => void;
    markArchived: (chain: string, address: string) => void;
    clearAll: () => void;
    /** Replace the whole cache (used by investigation load/restore). */
    hydrate: (entries: Record<string, FtmCacheEntry>) => void;
    /** Export the cache for inclusion in saved investigation state. */
    exportForSave: () => Record<string, FtmCacheEntry>;
}

const keyFor = (chain: string, address: string) =>
    `${(chain || '').toUpperCase()}:${address}`;

export const useFtmStore = create<FtmStoreState & FtmStoreActions>((set, get) => ({
    entries: {},

    getEntry: (chain, address) => {
        if (!chain || !address) return null;
        return get().entries[keyFor(chain, address)] || null;
    },

    setEntry: (chain, address, response) => {
        const key = keyFor(chain, address);
        set(state => ({
            entries: {
                ...state.entries,
                [key]: {
                    chain: (chain || '').toUpperCase(),
                    address,
                    response,
                    ranAt: new Date().toISOString(),
                    archived: false,
                },
            },
        }));
    },

    clearEntry: (chain, address) => {
        const key = keyFor(chain, address);
        set(state => {
            const next = { ...state.entries };
            delete next[key];
            return { entries: next };
        });
    },

    markArchived: (chain, address) => {
        const key = keyFor(chain, address);
        set(state => {
            const existing = state.entries[key];
            if (!existing) return state;
            return {
                entries: {
                    ...state.entries,
                    [key]: { ...existing, archived: true },
                },
            };
        });
    },

    clearAll: () => set({ entries: {} }),

    hydrate: (entries) => set({ entries: entries || {} }),

    exportForSave: () => get().entries,
}));
